import React from 'react'
import { Navigate, Outlet, useLocation, type Location } from 'react-router-dom'
import ServerUnavailablePage from '@/app/pages/ServerUnavailablePage'
import { resolvePostAuthRedirect } from '@/common/lib/safeRedirect'
import { useMe, type TAuth } from '../model/me'

type RedirectMode = 'append' | 'consume'

type GuardProps = React.PropsWithChildren<{
    rule: (auth: TAuth) => boolean
    admin?: boolean
    throwOutTo: string
    redirectMode: RedirectMode
}>

const getDestination = (location: Location, throwOutTo: string, redirectMode: RedirectMode): string => {
    if (redirectMode === 'consume') {
        return resolvePostAuthRedirect(location.search, throwOutTo)
    }

    const redirectTarget = `${location.pathname}${location.search}`
    return redirectTarget ? `${throwOutTo}?redirect=${encodeURIComponent(redirectTarget)}` : throwOutTo
}

const Guard = ({ rule, throwOutTo, redirectMode, children, admin }: GuardProps) => {
    const { isError, isSuccess, error, me } = useMe()
    const location = useLocation()
    const destination = getDestination(location, throwOutTo, redirectMode)

    if (error?.toString() === 'Error: error refreshing token') {
        if (rule(false)) {
            return <Navigate replace to={destination} />
        }
        return children
    }
    if (isError) {
        if (error?.code === 'ERR_NETWORK') {
            return <ServerUnavailablePage />
        }

        return <Navigate replace to={destination} />
    }
    if (isSuccess) {
        if (admin) {
            if (!me?.is_admin) {
                return <Navigate replace to={destination} />
            }
        } else if (rule(true)) {
            return <Navigate replace to={destination} />
        }

        return children
    }
}

type FinalGuardProps = Omit<GuardProps, 'rule' | 'redirectMode'>

const rules = {
    signedIn: (auth: TAuth) => auth,
    signedOut: (auth: TAuth) => !auth,
}

export const GuestGuard = (props: FinalGuardProps) => <Guard rule={rules.signedIn} redirectMode='consume' {...props} />

export const UserGuard = (props: FinalGuardProps) => <Guard rule={rules.signedOut} redirectMode='append' {...props} />

export const AdminGuard = (props: FinalGuardProps) => <Guard rule={rules.signedOut} redirectMode='append' {...props} />

export const RegistrationGuard = () => <Outlet />

export const AppGuard = () => <Outlet />
